import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import ArrowUpwardSharpIcon from '@mui/icons-material/ArrowUpwardSharp'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import InfoSharpIcon from '@mui/icons-material/InfoSharp'
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Theme,
  Typography,
} from '@mui/material'
import {DatePicker, PickersDay} from '@mui/x-date-pickers'
import {getSpacing} from '@progynyglobal/pg-ui-library'
import dayjs from 'dayjs'
import {useFormik} from 'formik'
import {makeStyles} from 'tss-react/mui'

import {FileInput} from '@progyny/components/Buttons/FileInput'
import ContactSupport from '@progyny/components/ContactSupport'
import {useResponsive} from '@progyny/hooks/useResponsive'
import ConfirmationModal from '@progyny/pages/Budget/PartnerAgreement/ConfirmationModal'
import {
  FormValues,
  getPASchema,
  initialValues,
} from '@progyny/pages/Budget/PartnerAgreement/PAFormModel'
import RenderCountrySelector from '@progyny/pages/Budget/PartnerAgreement/helpers/RenderCountrySelector'
import {VALID_MIMETYPES} from '@progyny/pages/Budget/Reimbursement/Documents'
import {ReimbursementMobileStepper} from '@progyny/pages/Budget/Reimbursement/ReimbursementMobileStepper'
import FileInfoList from '@progyny/pages/Budget/Reimbursement/helpers/FileInfoList'
import {Document} from '@progyny/store/claims'
import {useMeStore} from '@progyny/store/meStore'

import {theme} from '@root/theme'

type PAFormProps = {
  isReview: boolean
  onBackBtn?: () => void
  onPASubmit: (values: FormValues, document: File) => void
  fromProfile?: boolean
}

const useStyles = makeStyles<{isDownSM: boolean}>()(
  (theme: Theme, {isDownSM}) => ({
    textAreaField: {
      textarea: {
        height: `${26}px !important`,
        transition: 'height 0.25s ease-in',
        '&:focus': {
          height: `${106}px !important`,
        },
      },
    },
    dialogContent: {
      marginBottom: isDownSM ? getSpacing(32) : getSpacing(24),
    },
    confirmModal: {
      '& .MuiPaper-root': {
        maxWidth: isDownSM ? 327 : 650,
        '& .MuiTypography-root': {
          textAlign: isDownSM ? 'center' : 'left',
          paddingLeft: getSpacing(24),
          paddingRight: isDownSM ? getSpacing(24) : undefined,
          paddingTop: getSpacing(32),
        },
      },
    },
  })
)

const PAForm: React.FC<PAFormProps> = (props: PAFormProps) => {
  const {t} = useTranslation()
  const isDownSM = useResponsive('down', 'sm')
  const {classes} = useStyles({isDownSM})
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  const [partnerId, setPartnerId] = useState<Document[]>([])
  const user = useMeStore((s) => s.user)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const validationSchema = useMemo(() => getPASchema(t), [t])
  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  })

  const getError = (field: keyof FormValues) =>
    formik.touched[field] ? formik.errors[field] : undefined

  const isFileUploadDisabled =
    !formik.values.documentType || partnerId.length > 0

  const sharedTextFieldStyles = useMemo(
    () => ({
      bgcolor: props.isReview
        ? theme.palette.other.brandColors.disabledGray
        : theme.palette.other.brandColors.white,
      ...(props.isReview && {
        '& .MuiInputBase-input.Mui-disabled': {
          color: theme.palette.text.primary,
        },
      }),
    }),
    [props.isReview]
  )

  function onFileInputChange(event: React.FormEvent) {
    const target = event.target as HTMLInputElement
    const file = target.files![0]
    if (file && VALID_MIMETYPES.includes(file.type)) {
      setPartnerId([
        ...partnerId,
        {file: file, docType: formik.values.documentType},
      ])
    }
  }

  const navigateToPreview = (fileUrl: string) => {
    window.open(fileUrl, '_blank')
  }

  function handleSubmit() {
    setShowConfirmModal(true)
  }

  function confirmSubmit() {
    props.onPASubmit(formik.values, fileInputRef.current?.files?.[0]!)
    setShowConfirmModal(false)
  }

  function removeDocument(file: File) {
    setPartnerId(partnerId.filter((item) => item.file !== file))
  }

  return (
    <Stack
      sx={{mt: props?.fromProfile ? 3 : 0, width: '100%', overflow: 'hidden'}}
    >
      <Stack
        component={'form'}
        px={props.fromProfile ? 0 : {xs: 2, sm: '6rem'}}
        onSubmit={formik.handleSubmit}
      >
        {!props?.fromProfile && (
          <>
            <Typography fontSize={16} fontWeight={600}>
              {t('budget_partner_agreement_pa_form_information_about_you')}
            </Typography>
            <Grid container spacing={2} mt={5} mb={10}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  value={user?.firstName}
                  name='memberFname'
                  disabled
                  sx={{
                    bgcolor: theme.palette.other.brandColors.disabledGray,
                    '& .Mui-disabled': {color: theme.palette.text.primary},
                  }}
                  label={t(
                    'budget_partner_agreement_pa_form_member_first_name'
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name='memberLname'
                  disabled
                  sx={{
                    bgcolor: theme.palette.other.brandColors.disabledGray,
                    '& .Mui-disabled': {color: theme.palette.text.primary},
                  }}
                  value={user?.lastName}
                  label={t('budget_partner_agreement_pa_form_member_last_name')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name='companyName'
                  disabled
                  sx={{
                    bgcolor: theme.palette.other.brandColors.disabledGray,
                    '& .Mui-disabled': {color: theme.palette.text.primary},
                  }}
                  label={t(
                    'budget_partner_agreement_pa_form_member_company_name'
                  )}
                  value={user?.company?.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name='enumber'
                  disabled
                  sx={{
                    bgcolor: theme.palette.other.brandColors.disabledGray,
                    '& .Mui-disabled': {color: theme.palette.text.primary},
                  }}
                  label={t(
                    'budget_partner_agreement_pa_form_member_employee_number'
                  )}
                  value={user?.employeeNumber}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  error={!!getError('homeAddress')}
                  helperText={getError('homeAddress')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name='homeAddress'
                  label={t('budget_partner_agreement_pa_form_home_address')}
                  value={
                    props.isReview
                      ? user?.member?.privateAddress?.streetAddress
                      : formik.values.homeAddress
                  }
                  disabled={props.isReview}
                  sx={sharedTextFieldStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  error={!!getError('postalCode')}
                  helperText={getError('postalCode')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name='postalCode'
                  label={t('budget_partner_agreement_pa_form_postal_code')}
                  value={
                    props.isReview
                      ? user?.member?.privateAddress?.postcode
                      : formik.values.postalCode
                  }
                  disabled={props.isReview}
                  sx={sharedTextFieldStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RenderCountrySelector
                  handleOnChange={formik.handleChange}
                  values={formik.values}
                  errors={{country: getError('country')}}
                  isReview={props.isReview}
                  id='member-country'
                  field='country'
                  label={t('common_country')}
                  sharedTextFieldStyles={sharedTextFieldStyles}
                  reviewValue={
                    props.isReview
                      ? user?.member?.privateAddress?.country?.code || undefined
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  error={!!getError('city')}
                  helperText={getError('city')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name='city'
                  label={t('common_city')}
                  value={
                    props.isReview
                      ? user?.member?.privateAddress?.city
                      : formik.values.city
                  }
                  disabled={props.isReview}
                  sx={sharedTextFieldStyles}
                />
              </Grid>
            </Grid>
          </>
        )}
        <Alert
          sx={{
            width: 'fit-content',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
          }}
          severity='info'
        >
          {t('partner_agreement_msg')}
        </Alert>
        <Typography fontSize={16} fontWeight={600}>
          {props?.fromProfile
            ? t('budget_partner_agreement_pa_form_partner_s_details')
            : t(
                'budget_partner_agreement_pa_form_information_about_your_partner'
              )}
        </Typography>
        <Grid container spacing={2} mt={5}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              error={!!getError('partnerFname')}
              helperText={getError('partnerFname')}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name='partnerFname'
              label={t('budget_partner_agreement_pa_form_partner_first_name')}
              value={
                props.isReview
                  ? user?.member?.partnerDetails?.firstName
                  : formik.values.partnerFname
              }
              disabled={props.isReview}
              sx={sharedTextFieldStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              error={!!getError('partnerLname')}
              helperText={getError('partnerLname')}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name='partnerLname'
              label={t('budget_partner_agreement_pa_form_partner_last_name')}
              value={
                props.isReview
                  ? user?.member?.partnerDetails?.lastName
                  : formik.values.partnerLname
              }
              disabled={props.isReview}
              sx={sharedTextFieldStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label={t(
                'budget_partner_agreement_pa_form_partner_date_of_birth'
              )}
              renderDay={(day, _selectedDate, pickersDayProps) => {
                const {key, ...rest} = pickersDayProps
                return <PickersDay key={key} {...rest} />
              }}
              value={
                props.isReview
                  ? dayjs(user?.member?.partnerDetails?.birthDate)
                  : dayjs(formik.values.partnerDob)
              }
              onChange={(newDate) => {
                const nextValue = !newDate
                  ? ''
                  : newDate.isValid()
                    ? newDate.format('YYYY-MM-DD')
                    : 'Invalid Date'
                formik.setFieldValue('partnerDob', nextValue)
              }}
              inputFormat='DD-MM-YYYY'
              disabled={props.isReview}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  required
                  name='partnerDob'
                  onBlur={formik.handleBlur}
                  error={!!getError('partnerDob')}
                  helperText={getError('partnerDob')}
                  sx={sharedTextFieldStyles}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              error={!!getError('partnerEmail')}
              helperText={getError('partnerEmail')}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name='partnerEmail'
              label={t('budget_partner_agreement_pa_form_your_partner_s_email')}
              value={
                props.isReview
                  ? user?.member?.partnerDetails?.email
                  : formik.values.partnerEmail
              }
              disabled={props.isReview}
              sx={sharedTextFieldStyles}
            />
          </Grid>
        </Grid>
        <FormControl
          required
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            gap: 3,
            mt: 4,
          }}
        >
          <FormLabel
            id='partner-address-label'
            sx={{
              '&.MuiFormLabel-root': {
                color: '#251F1F',
              },
            }}
          >
            {t('budget_partner_agreement_pa_form_does_your_partner_live_with')}
          </FormLabel>
          <RadioGroup
            row
            name='isSameAddressForPartner'
            value={
              props.isReview
                ? user?.member?.partnerDetails?.sameHousehold
                  ? 'yes'
                  : 'no'
                : formik.values.isSameAddressForPartner
            }
            onChange={formik.handleChange}
          >
            <FormControlLabel
              disabled={props.isReview}
              value='yes'
              control={<Radio disableRipple />}
              label={t('common_yes')}
            />
            <FormControlLabel
              disabled={props.isReview}
              value='no'
              control={<Radio disableRipple />}
              label={t('common_no')}
            />
          </RadioGroup>
        </FormControl>
        {(props.isReview
          ? !user?.member?.partnerDetails?.sameHousehold
          : formik.values.isSameAddressForPartner === 'no') && (
          <Grid container spacing={2} mt={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                error={!!getError('partnerHomeAddress')}
                helperText={getError('partnerHomeAddress')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='partnerHomeAddress'
                label={t(
                  'budget_partner_agreement_pa_form_partner_home_address'
                )}
                placeholder={t('budget_pa_partner_address')}
                value={
                  props.isReview
                    ? user?.member?.privateAddress?.streetAddress
                    : formik.values.partnerHomeAddress
                }
                disabled={props.isReview}
                sx={sharedTextFieldStyles}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                error={!!getError('partnerCity')}
                helperText={getError('partnerCity')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='partnerCity'
                label={t('common_city')}
                placeholder={t('budget_pa_partner_city')}
                value={
                  props.isReview
                    ? user?.member?.privateAddress?.city
                    : formik.values.partnerCity
                }
                disabled={props.isReview}
                sx={sharedTextFieldStyles}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                error={!!getError('partnerPostalCode')}
                helperText={getError('partnerPostalCode')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name='partnerPostalCode'
                label={t(
                  'budget_partner_agreement_pa_form_partner_postal_code'
                )}
                value={
                  props.isReview
                    ? user?.member?.privateAddress?.postcode
                    : formik.values.partnerPostalCode
                }
                disabled={props.isReview}
                sx={sharedTextFieldStyles}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RenderCountrySelector
                values={formik.values}
                errors={{partnerCountry: getError('partnerCountry')}}
                sharedTextFieldStyles={sharedTextFieldStyles}
                handleOnChange={formik.handleChange}
                isReview={props.isReview}
                id='partner-country'
                field='partnerCountry'
                label={t('budget_partner_agreement_pa_form_partner_country')}
                reviewValue={
                  props.isReview
                    ? user?.member?.partnerDetails?.address?.country?.code ||
                      undefined
                    : undefined
                }
              />
            </Grid>
          </Grid>
        )}
        <FormControl
          required
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            gap: 3,
            mt: 4,
          }}
        >
          <FormLabel
            id='partner-company-label'
            sx={{
              '&.MuiFormLabel-root': {
                color: '#251F1F',
              },
            }}
          >
            {t(
              'budget_partner_agreement_pa_form_is_your_partner_also_employed'
            )}
          </FormLabel>
          <RadioGroup
            row
            name='partnerCompany'
            value={
              props.isReview
                ? user?.member?.partnerDetails?.sameEmployer
                  ? 'yes'
                  : 'no'
                : formik.values.partnerCompany
            }
            onChange={formik.handleChange}
          >
            <FormControlLabel
              disabled={props.isReview}
              value='yes'
              control={<Radio disableRipple />}
              label={t('common_yes')}
            />
            <FormControlLabel
              disabled={props.isReview}
              value='no'
              control={<Radio disableRipple />}
              label={t('common_no')}
            />
          </RadioGroup>
        </FormControl>
        {(props.isReview
          ? user?.member?.partnerDetails?.sameEmployer
          : formik.values.partnerCompany === 'yes') && (
          <Stack flexDirection={'row'} mt={3}>
            <TextField
              required
              error={!!getError('partnerEnumber')}
              helperText={getError('partnerEnumber')}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name='partnerEnumber'
              label={t(
                'budget_partner_agreement_pa_form_partner_s_employee_number'
              )}
              value={
                props.isReview
                  ? user?.member?.partnerDetails?.employeeNumber
                  : formik.values.partnerEnumber
              }
              disabled={props.isReview}
              sx={{
                ...sharedTextFieldStyles,
                flexBasis: {xs: '100%', sm: '50%'},
              }}
            />
          </Stack>
        )}
        <Box display={'flex'} flexDirection={'column'}>
          {!props.isReview && (
            <>
              <Box mb={{xs: 1, sm: 2}}>
                <Typography
                  fontSize={16}
                  fontWeight={600}
                  mb={{xs: 0}}
                  mt={{xs: 4, sm: 7}}
                >
                  {t(
                    'budget_partner_agreement_pa_form_upload_your_partner_s_id'
                  )}
                </Typography>
              </Box>
              <Grid container spacing={isDownSM ? 0 : 2}>
                <Grid item xs={12} md={12}>
                  <FormControl
                    variant={'outlined'}
                    fullWidth
                    classes={{root: classes.textAreaField}}
                  >
                    <TextField
                      required
                      fullWidth
                      inputProps={{maxLength: 200, 'data-testid': 'textarea'}}
                      name='documentType'
                      error={!!getError('documentType')}
                      helperText={getError('documentType')}
                      label={t('budget_reimbursement_documents_document_type')}
                      placeholder={t(
                        'budget_partner_agreement_pa_form_for_example_passport'
                      )}
                      value={formik.values.documentType}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={partnerId.length > 0}
                      variant='outlined'
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box
                mt={{xs: 2, sm: 3}}
                mb={{xs: 2, sm: 1}}
                display={'flex'}
                flexDirection={'row'}
                justifyContent={'flex-start'}
              >
                <FileInput
                  onChange={onFileInputChange}
                  startIcon={<ArrowUpwardSharpIcon />}
                  accept={VALID_MIMETYPES.join(',')}
                  disabled={isFileUploadDisabled}
                  size={'large'}
                  color={'primary'}
                  id={'file-id-input'}
                  fileInputRef={fileInputRef}
                >
                  {t('budget_reimbursement_documents_choose_file')}
                </FileInput>
              </Box>
            </>
          )}
          <Box
            width={'100%'}
            height={'100%'}
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'flex-start'}
            sx={{overflowX: 'hidden'}}
          >
            {partnerId.length > 0 && (
              <FileInfoList
                documents={partnerId!}
                removeFile={removeDocument}
              />
            )}
            {props.isReview && user?.member?.partnerDetails?.fileName && (
              <Box mt={7}>
                <Typography mb={3} fontSize={16} fontWeight={600}>
                  {t(
                    'budget_partner_agreement_pa_form_your_partner_s_id_document'
                  )}
                </Typography>
                <Stack flexDirection={'row'} gap={1} alignItems={'center'}>
                  <InfoSharpIcon sx={{fill: '#B6BAC8'}} />
                  <Typography fontSize={14} fontWeight={400}>
                    {user?.member?.partnerDetails?.docType}
                  </Typography>
                </Stack>
                <Stack
                  mt={2}
                  flexDirection={'row'}
                  gap={1}
                  alignItems={'center'}
                >
                  <FileCopyIcon color='primary' />
                  <Typography
                    onClick={() =>
                      navigateToPreview(
                        user?.member?.partnerDetails?.identityDocumentUrl!
                      )
                    }
                    sx={{cursor: 'pointer'}}
                    fontSize={14}
                    color={'primary'}
                    fontWeight={700}
                  >
                    {user?.member?.partnerDetails?.fileName}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
        {!props.isReview && (
          <>
            <Stack mt={10}>
              <Alert
                severity='error'
                sx={{bgcolor: theme.palette.other.brandColors.seashell}}
              >
                <Stack gap={1}>
                  <Typography fontSize={16} fontWeight={600}>
                    {t('budget_partner_agreement_pa_form_important_to_note')}
                  </Typography>
                  <Typography fontSize={16} fontWeight={400}>
                    {t(
                      'budget_partner_agreement_pa_form_please_note_that_going_to'
                    )}
                  </Typography>
                  <Typography fontSize={16} fontWeight={400}>
                    {t(
                      'budget_partner_agreement_pa_form_please_note_that_you_may'
                    )}
                  </Typography>
                </Stack>
              </Alert>
            </Stack>
            <ReimbursementMobileStepper
              activeStep={0}
              stepNumber={1}
              toCancelPath={props.onBackBtn}
              toContinuePath={() => formik.handleSubmit()}
              cancelButtonText={t('common_back')}
              cancelButtonAriaLabel={'back to budget page'}
              continueButtonText={t('common_submit')}
              continueButtonAriaLabel={'Submit partner agreement form'}
              isDisabled={partnerId?.length === 0 || !!formik.errors.partnerDob}
              showBackButton={!!props.onBackBtn}
            />
            {showConfirmModal && (
              <ConfirmationModal
                open={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={confirmSubmit}
                title={t(
                  'budget_partner_agreement_pa_form_are_you_sure_everything_is'
                )}
                content={t(
                  'budget_partner_agreement_pa_form_please_doublecheck_all_information_yo'
                )}
                className={classes.confirmModal}
                dialogContentClassName={classes.dialogContent}
              />
            )}
          </>
        )}
      </Stack>
      <ContactSupport
        textKey='budget_partner_agreement_pa_form_if_you_want_to_change'
        mb={3}
        px={{xs: 2, sm: '6rem'}}
        mt={15}
        flexDirection={{xs: 'column', sm: 'row'}}
        flexWrap='wrap'
      />
    </Stack>
  )
}

export default PAForm
